/**
 * Invoices API Client
 * Epic 10: Invoice Core
 */

import { api } from './client';

// ============================================
// TYPES (matching backend schema)
// ============================================

// #925 BE-enum alignment (55Y invoice-list type-enum-fe): a ListInvoicesQueryDto
// szűrő + a mapPrismaTypeToType (lista/detail-olvasás) STORNO+MINUSZOS-t is felvesz.
// A CREDIT_NOTE megmarad (CreateInvoiceDto ma is elfogadja, számla-létrehozás — külön
// concern, NEM ez a tétel) — csak ADDITÍV bővítés, nem breaking.
export type InvoiceType =
  | 'STANDARD'
  | 'ADVANCE'
  | 'FINAL'
  | 'PROFORMA'
  | 'CREDIT_NOTE'
  | 'CORRECTION'
  | 'STORNO'
  | 'MINUSZOS';
export type InvoiceStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'APPROVED'
  | 'SENT'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'VOIDED';
export type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'COD';

export interface InvoiceItem {
  id: string;
  lineNumber: number;
  productId?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPriceNet: number;
  vatRate: string;
  vatPercentage: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  /** Special VAT code (AAM, TAM, EU, EUK, MAA) — null for standard rates */
  vatCode?: string | null;
  // legacy/optional — nem minden invoice-DTO adja
  itemType?: string;
  referenceType?: string;
  referenceId?: string;
}

export interface Invoice {
  id: string;
  tenantId: string;
  invoiceNumber: string;
  prefix?: string;
  sequenceNumber?: number | null;
  partnerId: string;
  partnerName?: string;
  partnerAddress?: string;
  type: InvoiceType;
  status: InvoiceStatus;
  isConfidential?: boolean;
  visibleToRoles?: string[];

  // Dates
  invoiceDate: string;
  fulfillmentDate?: string;
  dueDate: string;
  deliveryDate?: string;
  paidAt?: string;

  // Amounts
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  paidAmount: number;
  discountAmount?: number;

  // Currency
  currency: string;
  exchangeRate?: number;

  // Payment
  paymentMethod?: PaymentMethod;
  paymentRef?: string;

  // NAV
  navStatus?: string;
  navTransactionId?: string;
  navSentAt?: string;
  navConfirmedAt?: string;
  navErrorMessage?: string;

  // PDF
  pdfUrl?: string;
  pdfGenAt?: string;
  sentAt?: string;
  sentTo?: string;

  // Void/Storno
  voidedInvoiceId?: string;
  voidReason?: string;
  voidedAt?: string;
  voidedBy?: string;

  // Correction (E-56)
  parentInvoiceId?: string;
  modificationIndex?: number;
  modificationReason?: string;
  referencedInvoiceNumber?: string;

  // E-számla (ADR-063)
  eInvoiceHash?: string;
  eInvoiceSentAt?: string;

  // Notes
  notes?: string;
  internalNotes?: string;

  // Audit
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;

  // Relations (populated)
  items?: InvoiceItem[];
  partner?: {
    id: string;
    name: string;
    companyName?: string;
    taxNumber?: string;
    address?: string;
    eInvoiceEnabled?: boolean;
    eInvoiceEmail?: string;
  };
}

export interface InvoiceListResponse {
  data: Invoice[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface InvoiceResponse {
  data: Invoice;
}

export interface InvoiceStats {
  total: number;
  draft: number;
  sent: number;
  paid: number;
  overdue: number;
  totalRevenue: number;
  unpaidTotal: number;
}

export interface InvoiceFilters {
  type?: InvoiceType;
  status?: InvoiceStatus;
  partnerId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of invoices with filters
 */
export async function getInvoices(filters: InvoiceFilters = {}): Promise<InvoiceListResponse> {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.partnerId) params.append('partnerId', filters.partnerId);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.search) params.append('search', filters.search);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/invoices?${queryString}` : '/invoices';

  // The controller returns PaginatedResult<IInvoice> with { items, total, page, pageSize }
  const response = await api.get<{
    items: Invoice[];
    total: number;
    page: number;
    pageSize: number;
  }>(endpoint);

  return {
    data: response.items ?? [],
    meta: {
      total: response.total ?? 0,
      page: response.page ?? 1,
      pageSize: response.pageSize ?? 20,
      hasMore: (response.page ?? 1) * (response.pageSize ?? 20) < (response.total ?? 0),
    },
  };
}

/**
 * Get single invoice by ID
 */
export async function getInvoiceById(id: string): Promise<InvoiceResponse> {
  const invoice = await api.get<Invoice>(`/invoices/${id}`);
  return { data: invoice };
}

/**
 * Get invoice statistics
 *
 * TODO(M-016/performance): Create a dedicated /invoices/stats backend endpoint using SQL
 * aggregation (SUM, COUNT with GROUP BY status). Current implementation fetches up to 1000
 * invoice records for client-side aggregation, which is both slow and silently truncates
 * at 1000 invoices. Financial aggregation should never be done client-side.
 */
export async function getInvoiceStats(): Promise<{ data: InvoiceStats }> {
  // Try dedicated stats endpoint first
  try {
    return await api.get<{ data: InvoiceStats }>('/invoices/stats');
  } catch {
    // Fallback: client-side aggregation (legacy — remove when backend endpoint exists)
  }

  const response = await getInvoices({ pageSize: 1000 });
  const invoices = response.data ?? [];

  const stats: InvoiceStats = {
    total: response.meta.total, // Use server total, not truncated page length
    draft: invoices.filter(i => i.status === 'DRAFT').length,
    sent: invoices.filter(i => i.status === 'SENT').length,
    paid: invoices.filter(i => i.status === 'PAID').length,
    overdue: invoices.filter(i => i.status === 'OVERDUE').length,
    totalRevenue: Math.round(
      invoices.filter(i => i.status === 'PAID').reduce((sum, i) => sum + Number(i.grossAmount), 0)
    ),
    unpaidTotal: Math.round(
      invoices
        .filter(i => i.status === 'SENT' || i.status === 'OVERDUE' || i.status === 'PARTIALLY_PAID')
        .reduce((sum, i) => sum + (Number(i.grossAmount) - Number(i.paidAmount)), 0)
    ),
  };

  return { data: stats };
}

/**
 * Create invoice
 */
export async function createInvoice(data: {
  partnerId: string;
  partnerName: string;
  partnerAddress: string;
  partnerTaxNumber?: string;
  type?: InvoiceType;
  invoiceDate?: string;
  fulfillmentDate?: string;
  dueDate?: string;
  paymentMethod: PaymentMethod;
  currency?: string;
  notes?: string;
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPriceNet: number;
    vatRate: string;
    productId?: string;
    discountPercent?: number;
  }>;
}): Promise<InvoiceResponse> {
  const invoice = await api.post<Invoice>('/invoices', data);
  return { data: invoice };
}

/**
 * Update invoice
 */
export async function updateInvoice(
  id: string,
  data: {
    dueDate?: string;
    notes?: string;
    internalNotes?: string;
    paymentMethod?: PaymentMethod;
    paymentReference?: string;
  }
): Promise<InvoiceResponse> {
  const invoice = await api.patch<Invoice>(`/invoices/${id}`, data);
  return { data: invoice };
}

/**
 * Delete invoice (draft only)
 */
export async function deleteInvoice(id: string): Promise<void> {
  return api.delete<void>(`/invoices/${id}`);
}

/**
 * Issue invoice (DRAFT -> ISSUED)
 */
export async function issueInvoice(id: string): Promise<InvoiceResponse> {
  // M-017 fix: Pass explicit empty object as patch body
  const invoice = await api.patch<Invoice>(`/invoices/${id}/issue`, {});
  return { data: invoice };
}

/**
 * Mark invoice as sent
 */
export async function markInvoiceAsSent(id: string): Promise<InvoiceResponse> {
  // M-017 fix: Pass explicit empty object as patch body
  const invoice = await api.patch<Invoice>(`/invoices/${id}/send`, {});
  return { data: invoice };
}

/**
 * Record payment on invoice
 */
export async function recordPayment(
  id: string,
  data: {
    amount: number;
    paymentDate?: string;
    paymentMethod?: PaymentMethod;
    reference?: string;
  }
): Promise<InvoiceResponse> {
  const invoice = await api.post<Invoice>(`/invoices/${id}/payment`, data);
  return { data: invoice };
}

/**
 * Create storno invoice
 */
export async function createStornoInvoice(
  id: string,
  data: {
    reason: string;
    partialItems?: Array<{ lineNumber: number; quantity: number }>;
    isProductReturn?: boolean;
    inspectionConfirmed?: boolean;
  }
): Promise<InvoiceResponse> {
  const invoice = await api.post<Invoice>(`/invoices/${id}/storno`, data);
  return { data: invoice };
}

/**
 * Create correction invoice (E-56)
 */
export async function createCorrectionInvoice(
  id: string,
  data: {
    reason: string;
    items: Array<{ lineNumber: number; quantity?: number; unitPriceNet?: number }>;
    fulfillmentDate?: string;
    dueDate?: string;
  }
): Promise<InvoiceResponse> {
  const invoice = await api.post<Invoice>(`/invoices/${id}/correction`, data);
  return { data: invoice };
}

/**
 * Get NAV status for invoice
 */
export async function getNavStatus(id: string): Promise<{
  data: {
    invoiceId: string;
    status: 'PENDING' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'ERROR';
    transactionId?: string;
    submittedAt?: string;
    acceptedAt?: string;
    errorMessage?: string;
  };
}> {
  return api.get(`/invoices/${id}/nav-status`);
}

/**
 * Invoice PDF template type (SALES-FG-4-9)
 */
export type InvoiceTemplate = 'STANDARD' | 'DETAILED' | 'PROFORMA';

export interface InvoiceTemplateInfo {
  id: InvoiceTemplate;
  name: string;
  description: string;
}

/**
 * Download invoice PDF (with optional template selection — SALES-FG-4-9)
 */
export async function downloadInvoicePdf(
  id: string,
  options?: { duplicate?: boolean; language?: 'hu' | 'en'; template?: InvoiceTemplate }
): Promise<Blob> {
  const params = new URLSearchParams();
  if (options?.duplicate) params.append('duplicate', 'true');
  if (options?.language) params.append('language', options.language);
  if (options?.template) params.append('template', options.template);

  const queryString = params.toString();
  const endpoint = queryString ? `/invoices/${id}/pdf?${queryString}` : `/invoices/${id}/pdf`;

  // M-002/M-003 fix: Use centralized API client with proper auth + token refresh
  return api.downloadBlob(endpoint);
}

/**
 * Get available invoice PDF templates (SALES-FG-4-9)
 */
export async function getInvoiceTemplates(): Promise<{ data: InvoiceTemplateInfo[] }> {
  return api.get('/invoices/templates');
}

// ============================================
// E-SZÁMLA (P-INV-2, ADR-063)
// ============================================

export interface EInvoiceSendResult {
  data: {
    success: boolean;
    hash: string;
    sentAt: string;
  };
}

/**
 * Send e-invoice (PDF+XML+SHA-256 HASH) to partner
 */
export async function sendEInvoice(
  id: string,
  recipientEmail: string
): Promise<EInvoiceSendResult> {
  return api.post(`/invoices/${id}/e-invoice/send`, { recipientEmail });
}

/**
 * Get e-invoice package preview (hash + xml preview + pdf size)
 */
export async function getEInvoicePackage(id: string): Promise<{
  data: { hash: string; xmlPreview: string; pdfSize: number };
}> {
  return api.get(`/invoices/${id}/e-invoice/package`);
}

// ============================================
// DUNNING (FIN-FG-4-5)
// ============================================

export type DunningLevel = 'NONE' | 'REMINDER' | 'WARNING' | 'FINAL_NOTICE';

export interface DunningInvoice {
  id: string;
  invoiceNumber: string;
  partnerName: string;
  dueDate: string;
  totalAmount: number;
  balanceDue: number;
  overdueDays: number;
  dunningLevel: DunningLevel;
  lastEscalatedAt?: string;
  escalationCount: number;
}

export interface DunningHistoryEntry {
  id: string;
  invoiceId: string;
  level: DunningLevel;
  action: string;
  note: string;
  createdAt: string;
  createdBy: string;
}

export interface DunningEscalateResult {
  invoiceId: string;
  previousLevel: string;
  currentLevel: string;
  overdueDays: number;
  action: string;
}

/**
 * Get overdue invoices with dunning status
 */
export async function getDunningStatus(): Promise<{ data: DunningInvoice[] }> {
  return api.get('/invoices/dunning-status');
}

/**
 * Manually escalate dunning for an invoice
 */
export async function escalateDunning(id: string): Promise<{ data: DunningEscalateResult }> {
  return api.post(`/invoices/${id}/dunning/escalate`, {});
}

/**
 * Get dunning history for an invoice
 */
export async function getDunningHistory(id: string): Promise<{ data: DunningHistoryEntry[] }> {
  return api.get(`/invoices/${id}/dunning-history`);
}

// ============================================
// HAVI SZÁMLA-EXPORT (55Y №3)
// ============================================

/**
 * Havi számla-export letöltése (XLSX vagy CSV) — 55Y №3 (havi zárás).
 *
 * Wire-kontraktus (pinnelt — a BE-endpoint még készül, Zeno-III):
 * GET /accounting/reports/invoices-export?month=YYYY-MM&format=xlsx|csv → bináris Blob.
 */
export async function exportInvoicesMonthly(month: string, format: 'xlsx' | 'csv'): Promise<Blob> {
  const params = new URLSearchParams();
  params.append('month', month);
  params.append('format', format);

  const endpoint = `/accounting/reports/invoices-export?${params.toString()}`;

  // M-002/M-003 fix mintája (downloadInvoicePdf): centralizált API-kliens auth + token-refresh.
  return api.downloadBlob(endpoint);
}
